
'use client';

import * as React from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CalendarDays, Clock, Info, Loader2, MapPin, Plus, Send, Trash2 } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { useAuth } from '@/context/auth-context';
import AuthGate from './auth-gate';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger, DialogClose } from './ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from './ui/form';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { format } from 'date-fns';
import type { Event } from '@/lib/types';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';


const createEventSchema = z.object({
    title: z.string().min(5, "Title must be at least 5 characters.").max(100),
    description: z.string().min(10, "Description must be at least 10 characters.").max(500),
    location: z.string().min(3, "Location is required.").max(100),
    date: z.string().refine((val) => {
        const parts = val.split('-').map(part => part.trim());
        if (parts.length !== 3) return false;
        const [day, month, year] = parts;
        if (day.length !== 2 || month.length !== 2 || year.length !== 4) return false;
        const dateStr = `${year}-${month}-${day}`;
        return !isNaN(Date.parse(dateStr));
    }, {
        message: "Invalid date format. Please use DD - MM - YYYY.",
    }),
});

const CreateEventForm = ({ onAddEvent }: { onAddEvent: (event: Omit<Event, 'id'|'author'|'createdAt'|'date'> & { date: string }) => void }) => {
    const { user } = useAuth();
    const [loading, setLoading] = React.useState(false);
    const form = useForm<z.infer<typeof createEventSchema>>({
        resolver: zodResolver(createEventSchema),
        defaultValues: {
            title: "",
            description: "",
            location: "",
            date: "",
        },
    });

    const onSubmit = async (values: z.infer<typeof createEventSchema>) => {
        if (!user) return;
        setLoading(true);
        
        // Pass the raw string value to the parent handler
        await onAddEvent(values);

        setLoading(false);
        form.reset();
        document.getElementById('close-create-event-dialog')?.click();
    };

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button className="w-full">
                    <Plus className="mr-2 h-4 w-4" /> Create Event
                </Button>
            </DialogTrigger>
            <DialogContent onPointerDownOutside={(event) => {
                const originalEvent = event.detail.originalEvent;
                const target = originalEvent.target as HTMLElement;
                if (target.closest('[data-radix-popper-content-wrapper]')) {
                    event.preventDefault();
                }
            }}>
                <DialogHeader>
                    <DialogTitle>Create a New Community Event</DialogTitle>
                    <DialogDescription>
                        Fill out the details below to share your event with the community.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField control={form.control} name="title" render={({ field }) => (
                            <FormItem>
                                <FormLabel>Event Title</FormLabel>
                                <FormControl><Input placeholder="e.g., Community Iftar" {...field} /></FormControl>
                                <FormMessage />
                            </FormItem>
                        )} />
                        <FormField control={form.control} name="description" render={({ field }) => (
                            <FormItem>
                                <FormLabel>Description</FormLabel>
                                <FormControl><Textarea placeholder="Tell us more about the event..." {...field} /></FormControl>
                                <FormMessage />
                            </FormItem>
                        )} />
                        <FormField control={form.control} name="location" render={({ field }) => (
                            <FormItem>
                                <FormLabel>Location</FormLabel>
                                <FormControl><Input placeholder="e.g., Local mosque or park" {...field} /></FormControl>
                                <FormMessage />
                            </FormItem>
                        )} />
                        <FormField control={form.control} name="date" render={({ field }) => (
                            <FormItem>
                                <FormLabel>Date</FormLabel>
                                <FormControl><Input placeholder="e.g., DD - MM - YYYY" {...field} /></FormControl>
                                <FormMessage />
                            </FormItem>
                        )} />
                        <DialogFooter>
                            <DialogClose asChild><Button type="button" variant="secondary" id="close-create-event-dialog">Cancel</Button></DialogClose>
                            <Button type="submit" disabled={loading}>
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                                Publish Event
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

const EventCard = ({ event, onDelete }: { event: Event; onDelete: (eventId: string) => void }) => {
    const { user } = useAuth();
    const canDelete = user && (user.uid === event.author.uid || user.isOwner);

    return (
        <Card>
            <CardHeader>
                <div className="flex justify-between items-start">
                    <div>
                        <CardTitle>{event.title}</CardTitle>
                        <CardDescription>Hosted by {event.author.name}</CardDescription>
                    </div>
                    <Avatar className="h-10 w-10">
                        <AvatarImage src={event.author.avatar} />
                        <AvatarFallback>{event.author.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                </div>
            </CardHeader>
            <CardContent>
                <p className="text-muted-foreground whitespace-pre-wrap">{event.description}</p>
            </CardContent>
            <CardFooter className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 text-sm text-muted-foreground">
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <CalendarDays className="w-4 h-4" />
                        <span>{format(event.date, 'EEEE, MMMM d, yyyy')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{event.location}</span>
                    </div>
                </div>
                {canDelete && (
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This will permanently delete the event. This action cannot be undone.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => onDelete(event.id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                )}
            </CardFooter>
        </Card>
    );
};

interface EventViewProps {
    events: Event[];
    onAddEvent: (event: Omit<Event, 'id' | 'author' | 'createdAt' | 'date'> & { date: string }) => void;
    onDeleteEvent: (eventId: string) => void;
}

export default function EventView({ events, onAddEvent, onDeleteEvent }: EventViewProps) {
    const { user } = useAuth();
    
    if (!user) {
        return <AuthGate />;
    }

    return (
        <div className="flex flex-col h-full bg-background">
            <header className="p-4 border-b sticky top-0 bg-background z-10">
                <h1 className="text-xl md:text-2xl font-bold text-foreground">Community Events</h1>
            </header>
            <ScrollArea className="flex-1">
                <div className="p-4 space-y-4">
                    <CreateEventForm onAddEvent={onAddEvent} />
                    {events.length > 0 ? (
                        events.map(event => (
                            <EventCard key={event.id} event={event} onDelete={onDeleteEvent} />
                        ))
                    ) : (
                        <Card className="text-center p-8">
                            <CardTitle>No Events Yet</CardTitle>
                            <CardDescription className="mt-2">
                                Be the first to create an event and bring the community together!
                            </CardDescription>
                        </Card>
                    )}
                </div>
            </ScrollArea>
            <footer className="p-4 border-t bg-background">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Info className="w-4 h-4" />
                    <div>
                        <p>Events are community-driven. Please verify details before attending.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
